/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { Sticky } from '@thd-olt-component-react/sticky';
import classNames from 'classnames';
import { DraggableDrawer } from './drawer/DraggableDrawer';
import { getPortalRootNode } from '../shared/utils/assistant-utils';
import { useZIndexManager } from '../hooks/useZIndexManager';
import './DrawerFrame.scss';

// DrawerFrame is the drawer's PLACEMENT only, deliberately content-agnostic — no providers/
// journey context inside. See .claude/context/drawer-layout.md.
export const DrawerFrame = ({
  drawerState, setDrawerState, offsetMargin, children,
}) => {
  const { lesser: isMobile } = useBreakpoint('lg');
  const portalRootNode = getPortalRootNode();

  useEffect(() => {
    const rootEl = document.getElementById('root');
    // Add/remove CSS class based on current breakpoint
    if (!isMobile && rootEl) {
      rootEl.classList.add('magic-apron-assistant-root');
    } else if (rootEl) {
      rootEl.classList.remove('magic-apron-assistant-root');
    }

    return () => {
      if (!isMobile && rootEl) {
        rootEl.classList.remove('magic-apron-assistant-root');
      }
    };
  }, [isMobile]);

  // Manage z-index of LiveChat elements when drawer state is OPEN
  useZIndexManager('#spr-live-chat-app > div', 900, drawerState === 'OPEN');

  const stickyClassnames = classNames('sui-w-full sui-left-0 sui-transition-[bottom]', {
    '!sui-duration-500': offsetMargin > 0
  });

  if (isMobile) {
    return (
      // StickyWithHeaderObserver has z-index of 899 that we can't override so need to wrap to be on top of header/footer
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-z-[998] sui-relative"
        data-testid="magic-apron-assistant"
        data-component="DrawerFrame"
      >
        {/* Scrim only — sizing lives in DraggableDrawer. See .claude/context/drawer-layout.md */}
        {drawerState === 'OPEN' && <div className="sui-fixed sui-inset-0 sui-bg-inverse/50" />}
        <Sticky
          className={stickyClassnames}
          position="bottom"
          stickyByDefault
          visibleByDefault
          offsetMargin={offsetMargin}
        >
          <DraggableDrawer
            drawerState={drawerState}
            setDrawerState={setDrawerState}
          >
            {children}
          </DraggableDrawer>
        </Sticky>
      </div>
    );
  }

  // Built once and either portaled or rendered in place, so the two desktop branches share
  // one markup and differ only in the createPortal call.
  const aside = (
    <aside
      id="magic-apron-assistant-drawer"
      data-testid="magic-apron-assistant"
      data-component="DrawerFrame"
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className="sui-sticky sui-flex sui-flex-col sui-h-[calc(100vh-169px)] sui-top-[169px] sui-ml-3 sui-mr-8 sui-border-solid sui-border-1 sui-border-primary"
    >
      {children}
    </aside>
  );

  return portalRootNode !== null ? createPortal(aside, portalRootNode) : aside;
};

DrawerFrame.propTypes = {
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: PropTypes.func.isRequired,
  offsetMargin: PropTypes.number.isRequired,
  children: PropTypes.node,
};
